import { partition } from 'lodash'
import React, { forwardRef, useRef } from 'react'

import { toastService } from '@utils/toast.service'
import { Icon } from './icon.component'
import { Button } from './mantine/button.component'

type FileUploadProps = {
  className?: string
  uploadTrigger?: (props: { handleClick: () => void }) => React.ReactNode
  onBlur?: () => void
  onFocus?: () => void
  error?: string
  readOnly?: boolean
  megabyteLimit?: number
  onChange?: (result: { files: File[]; tooLarge: File[] }) => void
  disableFileTypes?: ('pdf' | 'image')[]
  multiple?: boolean
  btnVariant?: 'primary' | 'secondary' | 'tertiary' | 'tertiary-dark' | 'input'
}

export const FileUpload = forwardRef<HTMLButtonElement, FileUploadProps>(
  (
    {
      className,
      uploadTrigger,
      onBlur,
      onFocus,
      error,
      readOnly,
      megabyteLimit = 2,
      onChange = () => {},
      disableFileTypes = [],
      multiple = false,
      btnVariant = 'primary',
    },
    ref,
  ) => {
    const hiddenFileInput = useRef<HTMLInputElement>(null)

    const disableImages = !disableFileTypes.includes('image')
    const disablePdfs = !disableFileTypes.includes('pdf')

    const fileTypesToAccept = [
      disableImages && 'image/jpeg',
      disableImages && 'image/png',
      disablePdfs && 'application/pdf',
    ].filter(type => type)

    function handleClick() {
      hiddenFileInput.current!.click()
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const files = [...e.currentTarget.files!]
      const oneMegaByte = 1048576
      const [tooLarge, justRight] = partition(files, file => {
        return file.size > oneMegaByte * megabyteLimit
      })

      if (tooLarge.length) {
        toastService.error(
          `One or more of the files you are uploading is too large`,
        )
      }

      onChange({
        files: justRight,
        tooLarge,
      })
    }

    return (
      <>
        {uploadTrigger ? (
          uploadTrigger({ handleClick })
        ) : (
          <div className={className || ''}>
            <div className="flex items-center">
              <Button
                ref={ref}
                variant={btnVariant}
                onClick={handleClick}
                disabled={readOnly}
                onBlur={onBlur}
                onFocus={onFocus}
                leftSection={
                  <Icon
                    size={14}
                    name="file-upload"
                  />
                }
              >
                Choose file{multiple ? 's' : ''}
              </Button>
              <div className="ml-3 font-medium">({megabyteLimit}MB limit)</div>
            </div>
            {error && <div className="mt-1 text-xl text-danger">{error}</div>}
          </div>
        )}
        <input
          accept={fileTypesToAccept.join(',')}
          type="file"
          hidden
          disabled={readOnly}
          ref={hiddenFileInput}
          onChange={handleChange}
          multiple={multiple}
        />
      </>
    )
  },
)
